import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { taskBucket } from "./taskUtils";

const REMINDER_HOUR = 9; // default time for tasks that have no time of their own
const PAYMENT_HOUR = 6; // unpaid bills, EMIs and instalments remind at 6 in the morning
const PAYMENT_NAG_DAYS = 14; // a reminder every morning for this many days, re-planned whenever the app opens
const MAX_PAYMENT_REMINDERS = 300; // Android caps how many alarms one app may keep
const CHANNEL_ID = "task-reminders";
const PAYMENT_CHANNEL_ID = "payment-reminders";
const SMALL_ICON = "ic_stat_task"; // android/app/src/main/res/drawable/ic_stat_task.xml
const TEST_ID = 2147000000;
const SUMMARY_ID = 2146000000; // the month-end summary, planned for the 1st of next month
const ALERT_ID_BASE = 2145000000; // one-off alerts shown straight away (budgets)
const SUMMARY_HOUR = 9;
const PAYMENT_ID_BASE = 1000000000; // bill reminder ids live above task ids

export const isNativeApp = () => Capacitor.isNativePlatform();

// A high-importance channel makes reminders pop up on screen, make a sound, and
// show their text on the lock screen (visibility public).
// Channels only exist on Android; iPhones have no equivalent.
const ensureChannel = async () => {
  if (Capacitor.getPlatform() !== "android") return;
  const common = { importance: 5, visibility: 1, vibration: true, lights: true };
  await LocalNotifications.createChannel({ id: CHANNEL_ID, name: "Task reminders", description: "Reminders for your tasks", ...common });
  await LocalNotifications.createChannel({ id: PAYMENT_CHANNEL_ID, name: "Payment reminders", description: "Bills, EMIs and investments that are due and not paid yet", ...common });
};

const at = (dateStr, timeStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = (timeStr || `${String(REMINDER_HOUR).padStart(2, "0")}:00`).split(":").map(Number);
  return new Date(y, m - 1, d, hh, mm, 0, 0);
};

// When should the reminder for this task go off? Overdue tasks nag once a day
// at 9am until they are done.
const reminderTime = (task, now = new Date()) => {
  const planned = at(task.dueDate, task.dueTime);
  if (planned > now) return planned;
  const next = new Date(now);
  next.setHours(REMINDER_HOUR, 0, 0, 0);
  if (next <= now) next.setDate(next.getDate() + 1);
  return next;
};

export const getReminderStatus = async () => {
  if (!isNativeApp()) return null;
  try {
    const perm = await LocalNotifications.checkPermissions();
    let exact = "granted";
    try {
      exact = (await LocalNotifications.checkExactNotificationSetting()).exact_alarm;
    } catch {
      // older Android versions have no such setting
    }
    return { notifications: perm.display, exact };
  } catch {
    return null;
  }
};

export const requestReminderPermission = async () => {
  if (!isNativeApp()) return null;
  await LocalNotifications.requestPermissions();
  return getReminderStatus();
};

// Opens Android's "Alarms & reminders" screen so reminders can fire at the exact minute.
export const openExactAlarmSettings = async () => {
  if (!isNativeApp()) return;
  try {
    await LocalNotifications.changeExactNotificationSetting();
  } catch {
    // ignore
  }
};

export const sendTestReminder = async () => {
  if (!isNativeApp()) return false;
  const status = await getReminderStatus();
  if (!status || status.notifications !== "granted") return false;
  await ensureChannel();
  await LocalNotifications.schedule({
    notifications: [
      {
        id: TEST_ID,
        title: "budgetsprint reminder",
        body: "This is what a task reminder looks like. Lock your phone to see it there too.",
        smallIcon: SMALL_ICON,
        channelId: CHANNEL_ID,
        schedule: { at: new Date(Date.now() + 8000), allowWhileIdle: true },
      },
    ],
  });
  return true;
};

// Calls back with "task", "bill" or "analysis" (the month summary and budget
// alerts) when a reminder is tapped, so the app can open the right place.
export const onReminderTapped = (callback) => {
  if (!isNativeApp()) return () => {};
  const handle = LocalNotifications.addListener("localNotificationActionPerformed", (event) => {
    const extra = event && event.notification && event.notification.extra;
    const kind = extra && extra.kind;
    callback(kind === "bill" ? "bill" : kind === "summary" || kind === "alert" ? "analysis" : "task");
  });
  return () => {
    handle.then((h) => h.remove()).catch(() => {});
  };
};

const pad2 = (n) => String(n).padStart(2, "0");
const isoOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Every unpaid bill (including EMIs and investment instalments) reminds at 6am on
// its due date, then again every morning until it is marked paid. A paid one drops
// out the next time the reminders are re-planned (which happens on every change).
const paymentReminders = (bills, { maskAmounts }, now = new Date()) => {
  const firstMorning = new Date(now);
  firstMorning.setHours(PAYMENT_HOUR, 0, 0, 0);
  if (firstMorning <= now) firstMorning.setDate(firstMorning.getDate() + 1);

  const unpaid = bills
    .filter((b) => b.status === "Pending")
    .sort((a, b) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0));

  const out = [];
  for (const bill of unpaid) {
    const [y, m, d] = bill.dueDate.split("-").map(Number);
    const dueMorning = new Date(y, m - 1, d, PAYMENT_HOUR, 0, 0, 0);
    const start = dueMorning > firstMorning ? dueMorning : firstMorning;
    const amount = maskAmounts ? "" : ` ₹${Number(bill.amount).toLocaleString("en-IN")}`;
    const dueLabel = new Date(y, m - 1, d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    for (let k = 0; k < PAYMENT_NAG_DAYS && out.length < MAX_PAYMENT_REMINDERS; k += 1) {
      const at = new Date(start);
      at.setDate(at.getDate() + k);
      const day = isoOf(at);
      out.push({
        id: PAYMENT_ID_BASE + (bill.id % 50000000) * 20 + k,
        title: `${bill.payee}${amount}`,
        body: day === bill.dueDate ? "Due today. Tap to mark it paid." : `Due since ${dueLabel} and not paid yet. Tap to mark it paid.`,
        smallIcon: SMALL_ICON,
        channelId: PAYMENT_CHANNEL_ID,
        extra: { kind: "bill", billId: bill.id },
        schedule: { at, allowWhileIdle: true },
      });
    }
  }
  return out;
};

// The month-end summary goes off at 9am on the 1st of next month. Its text is
// worked out now, from what's recorded so far, and refreshed every time reminders
// are re-planned (any change, and every time the app opens).
const summaryReminder = (summary, now = new Date()) =>
  summary
    ? [
        {
          id: SUMMARY_ID,
          title: `Your ${summary.title}`,
          body: summary.body,
          smallIcon: SMALL_ICON,
          channelId: PAYMENT_CHANNEL_ID,
          extra: { kind: "summary" },
          schedule: { at: new Date(now.getFullYear(), now.getMonth() + 1, 1, SUMMARY_HOUR, 0, 0, 0), allowWhileIdle: true },
        },
      ]
    : [];

// Shows a notification right away (a few seconds from now), for one-off alerts
// such as a budget running out. Does nothing in a normal browser or without permission.
export const notifyNow = async ({ key, title, body }) => {
  if (!isNativeApp()) return false;
  try {
    const perm = await LocalNotifications.checkPermissions();
    if (perm.display !== "granted") return false;
    await ensureChannel();
    // A stable id per alert, so the same one never shows twice at once.
    let hash = 0;
    for (const ch of key) hash = (hash * 31 + ch.charCodeAt(0)) % 900000;
    await LocalNotifications.schedule({
      notifications: [
        { id: ALERT_ID_BASE + hash, title, body, smallIcon: SMALL_ICON, channelId: PAYMENT_CHANNEL_ID, extra: { kind: "alert" }, schedule: { at: new Date(Date.now() + 2000), allowWhileIdle: true } },
      ],
    });
    return true;
  } catch {
    return false;
  }
};

// Re-plans every reminder on the phone from the current tasks and bills (plus the
// month-end summary, if given). Safe to call whenever either changes; it does
// nothing in a normal browser.
export const syncReminders = async ({ tasks, bills = [], maskAmounts = false, summary = null }) => {
  if (!isNativeApp()) return;
  try {
    let perm = await LocalNotifications.checkPermissions();
    if (perm.display === "prompt" || perm.display === "prompt-with-rationale") {
      perm = await LocalNotifications.requestPermissions();
    }
    if (perm.display !== "granted") return;

    await ensureChannel();

    const pending = await LocalNotifications.getPending();
    // Test and one-off alerts are left alone; everything else is planned afresh.
    const stale = pending.notifications.filter((n) => n.id !== TEST_ID && (n.id < ALERT_ID_BASE || n.id >= ALERT_ID_BASE + 1000000));
    if (stale.length) await LocalNotifications.cancel({ notifications: stale.map((n) => ({ id: n.id })) });

    const now = new Date();
    const notifications = tasks
      .filter((t) => {
        const b = taskBucket(t);
        return b === "todo" || b === "later";
      })
      .map((t) => ({
        id: t.id,
        title: t.title,
        body: t.description || (t.members.length > 1 ? "A shared task is due." : "This task is due."),
        smallIcon: SMALL_ICON,
        channelId: CHANNEL_ID,
        extra: { kind: "task", taskId: t.id },
        schedule: { at: reminderTime(t, now), allowWhileIdle: true },
      }))
      .concat(paymentReminders(bills, { maskAmounts }, now))
      .concat(summaryReminder(summary, now));

    if (notifications.length) await LocalNotifications.schedule({ notifications });
  } catch {
    // Reminders are a convenience; never break the app over them.
  }
};

export const cancelAllReminders = async () => {
  if (!isNativeApp()) return;
  try {
    const pending = await LocalNotifications.getPending();
    if (pending.notifications.length) await LocalNotifications.cancel({ notifications: pending.notifications.map((n) => ({ id: n.id })) });
  } catch {
    // ignore
  }
};
